import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../config/firebase';
import { seatService, bookingService } from '../services/api.service';
import './CheckoutPage.css';
import CreditCard from '../components/CreditCard';
import CheckoutTimer from '../components/CheckoutTimer';
import AlertModal from '../components/AlertModal';

const formLabels = {
    firstName: 'Ad',
    lastName: 'Soyad',
    email: 'E-posta',
    phone: 'Telefon',
    cardNumber: 'Kart Numarası',
    expiryDate: 'Son Kullanma Tarihi',
    cvv: 'Güvenlik Kodu',
    nameOnCard: 'Kart Üzerindeki İsim'
};

const CheckoutPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { eventId, subEventId } = useParams();
    const [user] = useAuthState(auth);
    
    // Add refs to store IDs
    const eventIdRef = useRef(eventId);
    const subEventIdRef = useRef(subEventId);
    
    // Update refs when params change
    useEffect(() => {
        eventIdRef.current = eventId;
        subEventIdRef.current = subEventId;
    }, [eventId, subEventId]);

    const [error, setError] = useState(null);
    const [isCvvFocused, setIsCvvFocused] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [timerStartTime, setTimerStartTime] = useState(() => {
        const subEventId = location.state?.event?.sub_events?.[0]?._id;
        if (!subEventId) return null;
        const stored = sessionStorage.getItem(`timer_${subEventId}`);
        return stored ? new Date(stored) : null;
    });
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        cardNameT: '',
        cardNumberT: '',
        idAy: '',
        idYil: '',
        cardCvvT: ''
    });
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');

    // Destructure with default empty values
    const { event, selectedSeats = [], totalAmount, reservationId } = location.state || {};

    // Get event IDs from both URL params and event object
    const currentEventId = eventId || event?.id;
    const currentSubEventId = subEventId || event?.sub_events?.[0]?._id;

    // Store IDs in refs
    useEffect(() => {
        eventIdRef.current = currentEventId;
        subEventIdRef.current = currentSubEventId;
    }, [currentEventId, currentSubEventId]);

    // Consolidated navigation and cleanup effect
    useEffect(() => {
        const sessionId = localStorage.getItem('sessionId');

        console.log('[Checkout] Component mounted', {
            hasSelectedSeats: !!location.state?.selectedSeats,
            selectedSeatsCount: location.state?.selectedSeats?.length,
            event: location.state?.event,
            sessionId,
            eventId: currentEventId,
            subEventId: currentSubEventId
        });

        return () => {
            console.log('[Checkout] Component unmounting', {
                isNavigatingToSeating: window.isNavigatingToSeating,
                isNavigatingToConfirmation: window.isNavigatingToConfirmation,
                selectedSeatsCount: selectedSeats?.length,
                sessionId,
                eventId: currentEventId,
                subEventId: currentSubEventId
            });

            // Release seats if we're not completing a purchase
            if (!window.isNavigatingToConfirmation && 
                selectedSeats?.length > 0 && 
                currentEventId && 
                currentSubEventId && 
                sessionId) {
                
                console.log('[Checkout] Releasing seats on unmount', {
                    selectedSeats,
                    eventId: currentEventId,
                    subEventId: currentSubEventId,
                    sessionId
                });

                const formattedSeats = selectedSeats.map(seat => 
                    typeof seat === 'string' ? seat : seat.label?.toString() || seat.toString()
                );

                seatService.releaseSeats(
                    currentEventId,
                    currentSubEventId,
                    formattedSeats,
                    sessionId
                ).then(() => {
                    console.log('[Checkout] Successfully released seats');
                    sessionStorage.removeItem(`timer_${currentSubEventId}`);
                }).catch(error => {
                    console.error('[Checkout] Failed to release seats:', {
                        error,
                        sessionId,
                        eventId: currentEventId,
                        subEventId: currentSubEventId,
                        seats: formattedSeats
                    });
                });
            } else {
                console.log('[Checkout] Skipping seat release', {
                    isNavigatingToSeating: window.isNavigatingToSeating,
                    isNavigatingToConfirmation: window.isNavigatingToConfirmation,
                    hasSelectedSeats: selectedSeats?.length > 0,
                    hasSessionId: !!sessionId,
                    eventId: currentEventId,
                    subEventId: currentSubEventId
                });
            }
        };
    }, [selectedSeats, currentEventId, currentSubEventId]);

    // Consolidated back navigation handler
    const handleBackClick = async (e) => {
        e.preventDefault();
        const sessionId = localStorage.getItem('sessionId');
        
        console.log('[Checkout] Back button clicked, releasing seats and navigating', {
            selectedSeats: location.state?.selectedSeats,
            eventId: currentEventId,
            subEventId: currentSubEventId,
            sessionId
        });
        
        if (!currentEventId || !currentSubEventId) {
            console.error('[Checkout] Missing event IDs for navigation', {
                urlEventId: eventId,
                urlSubEventId: subEventId,
                stateEventId: event?.id,
                stateSubEventId: event?.sub_events?.[0]?._id
            });
            navigate('/');
            return;
        }

        try {
            // First release the seats
            if (selectedSeats?.length > 0 && sessionId) {
                const formattedSeats = selectedSeats.map(seat => 
                    typeof seat === 'string' ? seat : seat.label?.toString() || seat.toString()
                );

                await seatService.releaseSeats(
                    currentEventId,
                    currentSubEventId,
                    formattedSeats,
                    sessionId
                );
                console.log('[Checkout] Successfully released seats before navigation');
                sessionStorage.removeItem(`timer_${currentSubEventId}`);
            }

            // Then navigate back without preserving seats
            window.isNavigatingToSeating = true;
            window.isNavigatingToCheckout = false;
            window.isNavigatingToConfirmation = false;
            
            navigate(`/seating/${currentEventId}/${currentSubEventId}`, {
                state: {
                    preserveSeats: false,  // Don't preserve seats when going back
                    selectedSeats: []  // Clear selected seats
                }
            });
        } catch (error) {
            console.error('[Checkout] Failed to release seats before navigation:', {
                error,
                sessionId,
                eventId: currentEventId,
                subEventId: currentSubEventId
            });
            // Navigate anyway to prevent user from being stuck
            navigate(`/seating/${currentEventId}/${currentSubEventId}`);
        }
    };

    // Handle timer expiration
    const handleTimerExpired = async () => {
        const currentEventId = eventIdRef.current;
        const currentSubEventId = subEventIdRef.current;
        const sessionId = localStorage.getItem('sessionId');

        console.log('[Checkout] Timer expired', {
            selectedSeats,
            eventId: currentEventId,
            subEventId: currentSubEventId,
            sessionId
        });

        if (selectedSeats?.length > 0 && currentSubEventId && sessionId) {
            try {
                await seatService.releaseSeats(
                    currentEventId,
                    currentSubEventId,
                    selectedSeats.map(seat => seat.label.toString()),
                    sessionId
                );
                sessionStorage.removeItem(`timer_${currentSubEventId}`);
                navigate(`/seating/${currentEventId}/${currentSubEventId}`);
            } catch (error) {
                console.error('[Checkout] Error releasing seats on timer expiry:', error);
            }
        }
    };

    // Remove redundant effects
    useEffect(() => {
        return () => {
            if (!window.isNavigatingToSeating && !window.isNavigatingToConfirmation) {
                window.isNavigatingToCheckout = false;
            }
        };
    }, []);

    // Add this effect to handle browser back button
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            // Only handle navigation if we're not completing purchase
            if (!window.isNavigatingToConfirmation) {
                const subEventId = event?.sub_events?.[0]?._id;
                const eventId = event?.id;
                
                if (eventId && subEventId) {
                    window.isNavigatingToCheckout = false;
                    window.isNavigatingToSeating = true;
                    navigate(`/seating/${eventId}/${subEventId}`, {
                        state: {
                            preserveSeats: true,
                            selectedSeats: selectedSeats
                        }
                    });
                }
            }
        };

        // Only add the event listener if we're not completing purchase
        if (!window.isNavigatingToConfirmation) {
            window.addEventListener('beforeunload', handleBeforeUnload);
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }
    }, [event, selectedSeats, navigate]);

    // Validation and initialization effect
    useEffect(() => {
        if (!location.state || !event || !selectedSeats || !totalAmount) {
            console.log('Missing required data:', { 
                state: location.state,
                event,
                selectedSeats,
                totalAmount 
            });
            navigate('/');
            return;
        }

        // Initialize timer if not already set
        const subEventId = event?.sub_events?.[0]?._id;
        if (subEventId) {
            const stored = sessionStorage.getItem(`timer_${subEventId}`);
            if (stored) {
                setTimerStartTime(new Date(stored));
            }
        }
    }, [location.state, event, selectedSeats, totalAmount, navigate]);

    // Handle beforeunload with session ID
    useEffect(() => {
        const handleBeforeUnload = async (e) => {
            const currentEventId = eventIdRef.current;
            const currentSubEventId = subEventIdRef.current;
            const sessionId = localStorage.getItem('sessionId');
            
            console.log('[Checkout] beforeunload triggered', {
                isNavigatingToSeating: window.isNavigatingToSeating,
                isNavigatingToConfirmation: window.isNavigatingToConfirmation,
                eventId: currentEventId,
                subEventId: currentSubEventId,
                sessionId
            });
            
            if (!window.isNavigatingToConfirmation && 
                !window.isNavigatingToSeating && 
                currentEventId && 
                currentSubEventId && 
                selectedSeats?.length > 0 &&
                sessionId) {
                
                try {
                    const formattedSeats = selectedSeats.map(seat => 
                        typeof seat === 'string' ? seat : seat.label?.toString() || seat.toString()
                    );
                    
                    await seatService.releaseSeats(
                        currentEventId, 
                        currentSubEventId, 
                        formattedSeats,
                        sessionId
                    );
                    sessionStorage.removeItem(`timer_${currentSubEventId}`);
                } catch (error) {
                    console.error('[Checkout] Failed to release seats:', {
                        error,
                        sessionId,
                        eventId: currentEventId,
                        subEventId: currentSubEventId
                    });
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [selectedSeats]);

    // Add validation function
    const isFormValid = () => {
        // Check contact information
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
            return false;
        }
        
        // Check credit card information
        if (!formData.cardNameT || !formData.cardNumberT || !formData.idAy || 
            !formData.idYil || !formData.cardCvvT) {
            return false;
        }

        // Validate card number format
        const cardNumberWithoutSpaces = formData.cardNumberT.replace(/\s/g, '');
        if (cardNumberWithoutSpaces.length !== 16 || !/^\d+$/.test(cardNumberWithoutSpaces)) {
            return false;
        }

        // Validate CVV
        if (formData.cardCvvT.length !== 3 || !/^\d+$/.test(formData.cardCvvT)) {
            return false;
        }

        return true;
    };

    // Early return after all hooks
    if (!location.state || !event || !selectedSeats || !totalAmount) {
        return <div>Redirecting...</div>;
    }

    const serviceFee = totalAmount * 0.12;
    const total = totalAmount + serviceFee;

    const handlePayment = async (e) => {
        e.preventDefault();
        
        try {
            setIsProcessing(true);
            setError(null);

            // Validate form data
            const missingFields = [];
            // Contact information
            if (!formData.firstName) missingFields.push('Ad');
            if (!formData.lastName) missingFields.push('Soyad');
            if (!formData.email) missingFields.push('E-posta');
            if (!formData.phone) missingFields.push('Telefon');
            
            // Credit card information
            if (!formData.cardNameT) missingFields.push('Kart Üzerindeki İsim');
            if (!formData.cardNumberT) missingFields.push('Kart Numarası');
            if (!formData.idAy) missingFields.push('Son Kullanma Ay');
            if (!formData.idYil) missingFields.push('Son Kullanma Yıl');
            if (!formData.cardCvvT) missingFields.push('Güvenlik Kodu');

            if (missingFields.length > 0) {
                const missingFieldsText = missingFields.join(',\n');
                showAlert(`Lütfen aşağıdaki zorunlu alanları doldurun:\n${missingFieldsText}`);
                setIsProcessing(false);
                throw new Error('Please fill in all required fields');
            }

            // Validate card number format (should be 16 digits)
            const cardNumberWithoutSpaces = formData.cardNumberT.replace(/\s/g, '');
            if (cardNumberWithoutSpaces.length !== 16 || !/^\d+$/.test(cardNumberWithoutSpaces)) {
                showAlert('Lütfen geçerli bir kredi kartı numarası girin');
                setIsProcessing(false);
                throw new Error('Invalid card number');
            }

            // Validate CVV (should be 3 digits)
            if (formData.cardCvvT.length !== 3 || !/^\d+$/.test(formData.cardCvvT)) {
                showAlert('Lütfen geçerli bir güvenlik kodu girin');
                setIsProcessing(false);
                throw new Error('Invalid CVV');
            }

            // Get the selected seats and validate
            const selectedSeats = location.state?.selectedSeats;
            if (!selectedSeats?.length) {
                showAlert('Lütfen koltuk seçimi yapın');
                throw new Error('No seats selected');
            }

            // Verify seats are still available
            const subEventId = event?.sub_events?.[0]?._id;
            const seatStatus = await seatService.getSeatStatus(event.id, subEventId);
            
            const unavailableSeats = selectedSeats.filter(seat => {
                const status = seatStatus[seat.label];
                return status && 
                       status.status !== 'available' && 
                       !(status.status === 'reserved' && status.is_current_session);
            });

            if (unavailableSeats.length > 0) {
                throw new Error('Seçtiğiniz koltuklar artık müsait değil');
            }

            // Format the booking data
            const bookingData = {
                event_id: event.id,
                sub_event_id: subEventId,
                customer_first_name: formData.firstName.trim(),
                customer_last_name: formData.lastName.trim(),
                guest_email: formData.email.trim(),
                guest_phone: formData.phone.replace(/\D/g, ''),
                tickets: selectedSeats.map(seat => ({
                    ticket_type_id: seat.ticket_type_id || seat.ticketTypeId || event.ticket_types[0].id,
                    seats: [seat.label],
                    quantity: 1,
                    unit_price: Number(seat.price)
                })),
                total_amount: Number(total),
                session_id: localStorage.getItem('sessionId'),
                status: 'pending'
            };

            // Log the booking data for debugging
            console.log('Creating booking with data:', bookingData);

            // Set navigation flags
            window.isNavigatingToConfirmation = true;
            window.isNavigatingToCheckout = false;
            window.isNavigatingToSeating = false;

            // Create the booking
            const booking = await bookingService.createBooking(bookingData);

            if (!booking) {
                throw new Error('Rezervasyon oluşturulamadı');
            }

            // Clear timer and navigate to confirmation
            sessionStorage.removeItem(`timer_${subEventId}`);
            
            navigate('/payment-confirmation', {
                state: {
                    event,
                    selectedSeats,
                    totalAmount: total,
                    paymentDetails: {
                        name: formData.cardNameT,
                        email: formData.email,
                        phone: formData.phone,
                        bookingId: booking.id || booking._id
                    },
                    booking: booking
                },
                replace: true
            });
        } catch (error) {
            console.error('Payment process failed:', error);
            window.isNavigatingToConfirmation = false;
            setIsProcessing(false);
            
            // Show appropriate error message
            if (error.message.includes('no longer available')) {
                const subEventId = event?.sub_events?.[0]?._id;
                const eventId = event?.id;
                if (eventId && subEventId) {
                    navigate(`/seating/${eventId}/${subEventId}`);
                }
            } else {
                setError(error.message === 'Please fill in all required fields' 
                    ? 'Lütfen tüm zorunlu alanları doldurun' 
                    : error.message || 'Ödeme işlemi başarısız oldu. Lütfen tekrar deneyin.');
            }
        }
    };

    const showAlert = (message) => {
        setAlertMessage(message);
        setIsAlertOpen(true);
    };

    return (
        <div className="checkout-page">
            <button 
                onClick={handleBackClick}
                className="back-button"
                style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}
            >
                ← Geri
            </button>
            
            {timerStartTime && (
                <div className="checkout-timer-wrapper">
                    <CheckoutTimer 
                        startTime={timerStartTime}
                        onTimeout={handleTimerExpired}
                    />
                </div>
            )}
            <div className="checkout-content">
                <div className="checkout-left">
                    <div className="section iletisim-bilgileri">
                        <h3>İletişim Bilgileri</h3>
                        <p className="info-text">Biletiniz aşağıdaki iletişim bilgilerine göre hazırlanacaktır. Lütfen doğru bilgiler girdiğinizden emin olunuz.</p>
                        
                        <div className="form-row">
                            <div className="form-group">
                                <label>Adı</label>
                                <input 
                                    type="text" 
                                    placeholder="Adı"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                                />
                            </div>
                            <div className="form-group">
                                <label>Soyadı</label>
                                <input 
                                    type="text" 
                                    placeholder="Soyadı"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>E-Mail</label>
                                <input 
                                    type="email" 
                                    placeholder="E-Mail"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                />
                            </div>
                            <div className="form-group">
                                <label>Cep Telefonu (GSM)</label>
                                <div className="phone-input">
                                    <div className="country-code">
                                        <img src="/tr-flag.png" alt="" />
                                        <span>+90</span>
                                    </div>
                                    <input 
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="section odeme-yontemleri">
                        <h3>Ödeme Yöntemleri</h3>
                        <div className="payment-method">
                            <img src="/card-icon.png" alt="" />
                            <span>Banka / Kredi Kartı</span>
                        </div>
                        
                        <div className="odemeYN_Govde-sol--kredikarti--bilgiler">
                            <div className="odemeYN_Govde-sol--kredikarti--bilgiler--kart">
                                <ul>
                                    <li>
                                        <span>Kart Üzerindeki İsim</span>
                                        <input 
                                            type="text" 
                                            id="cardNameT" 
                                            value={formData.cardNameT}
                                            onChange={(e) => setFormData({...formData, cardNameT: e.target.value})}
                                        />
                                    </li>
                                    <li>
                                        <span>Kart Numarası</span>
                                        <input 
                                            type="text"
                                            id="cardNumberT"
                                            maxLength="19"
                                            value={formData.cardNumberT}
                                            onChange={(e) => setFormData({...formData, cardNumberT: e.target.value})}
                                        />
                                    </li>
                                    <li>
                                        <div>
                                            <span>Son Kullanma Tarihi</span>
                                            <div className="expiry-date-selects">
                                                <select 
                                                    id="idAy" 
                                                    value={formData.idAy}
                                                    onChange={(e) => setFormData({...formData, idAy: e.target.value})}
                                                >
                                                    <option value="">Ay</option>
                                                    {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                                                        <option key={month} value={month.toString().padStart(2, '0')}>
                                                            {month.toString().padStart(2, '0')}
                                                        </option>
                                                    ))}
                                                </select>
                                                <select 
                                                    id="idYil" 
                                                    value={formData.idYil}
                                                    onChange={(e) => setFormData({...formData, idYil: e.target.value})}
                                                >
                                                    <option value="">Yıl</option>
                                                    {Array.from({length: 10}, (_, i) => new Date().getFullYear() + i).map(year => (
                                                        <option key={year} value={year}>
                                                            {year}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <span>Güvenlik Kodu</span>
                                                <input 
                                                    type="text"
                                                    id="cardCvvT"
                                                    maxLength="3"
                                                    value={formData.cardCvvT}
                                                    onChange={(e) => setFormData({...formData, cardCvvT: e.target.value})}
                                                    onFocus={() => setIsCvvFocused(true)}
                                                    onBlur={() => setIsCvvFocused(false)}
                                                />
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <CreditCard 
                                cardNumber={formData.cardNumberT}
                                nameOnCard={formData.cardNameT}
                                expiryMonth={formData.idAy}
                                expiryYear={formData.idYil}
                                cvv={formData.cardCvvT}
                                isFlipped={isCvvFocused}
                            />
                        </div>
                       
                    </div>
                </div>

                <div className="checkout-right">
                    <div className="order-summary">
                        <div className="event-summary">
                            <div className="event-image-wrapper">
                                <img 
                                    src={event?.image_url} 
                                    alt={event?.title}
                                    onError={(e) => {
                                        console.error('Image failed to load:', event?.image_url);
                                        e.target.src = '/placeholder-event.jpg';
                                    }}
                                />
                            </div>
                            <div className="event-details">
                                <h3>{event?.title}</h3>
                                <p><i className="fas fa-map-marker-alt"></i> {event?.sub_events?.[0]?.location?.venue || event?.location?.venue}</p>
                                <p><i className="far fa-calendar"></i> {new Date(event?.sub_events?.[0]?.date).toLocaleDateString('tr-TR', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                })}</p>
                                <p><i className="far fa-clock"></i> {new Date(event?.sub_events?.[0]?.date).toLocaleTimeString('tr-TR', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}</p>
                            </div>
                        </div>

                        <div className="selected-tickets">
                            <h4>Seçilen Koltuklar</h4>
                            {selectedSeats.map((seat, index) => (
                                <div key={index} className="ticket-item">
                                    <div className="ticket-details">
                                        <span className="seat-number">Koltuk {seat.label}</span>
                                        <span className="seat-category">{seat.category}</span>
                                    </div>
                                    <span className="ticket-price">{seat.price} TL</span>
                                </div>
                            ))}
                        </div>

                        <div className="price-summary">
                            <div className="price-row">
                                <span>Bilet Bedeli</span>
                                <span>{totalAmount?.toFixed(2)} TL</span>
                            </div>
                            <div className="price-row">
                                <span>Hizmet Bedeli</span>
                                <span>{serviceFee?.toFixed(2)} TL</span>
                            </div>
                            <div className="price-row total">
                                <span>Toplam</span>
                                <span>{total?.toFixed(2)} TL</span>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handlePayment}>
                        <button 
                            type="submit" 
                            className={`payment-button ${!isFormValid() ? 'disabled' : ''}`}
                            disabled={isProcessing || !isFormValid()}
                        >
                            {isProcessing ? 'İşleniyor...' : 'Ödeme Yap'}
                        </button>
                    </form>
                </div>
            </div>
            {isAlertOpen && (
                <AlertModal
                    message={alertMessage}
                    onClose={() => setIsAlertOpen(false)}
                />
            )}
        </div>
    );
};

export default CheckoutPage; 